import type {
    SpreadsheetAutoFilter,
    SpreadsheetAutoFilterCondition,
    SpreadsheetAutoFilterConditionJoin,
    SpreadsheetAutoFilterOperator,
    SpreadsheetCellValue,
    SpreadsheetRangeAddress,
    SpreadsheetRangeCell,
    SpreadsheetTableDefinition,
} from '../protocol/spreadsheetEngineProtocol';
import { colToLetter } from '../utils/columnUtils';

export type AutoFilterVisibleBounds = {
    startCol: number;
    endCol: number;
};

export type SpreadsheetUiRange = {
    startCol: number;
    startRow: number;
    endCol: number;
    endRow: number;
};

export type TableOverlayVisibleBounds = AutoFilterVisibleBounds & {
    startRow: number;
    endRow: number;
};

export type AutoFilterViewport = {
    width: number;
    height: number;
};

export type AutoFilterScreenRect = {
    x: number;
    y: number;
    w: number;
    h: number;
};

export type AutoFilterHeaderButtonProjection = {
    col: number;
    row: number;
    x: number;
    y: number;
    filtered: boolean;
    range: SpreadsheetRangeAddress;
    source: 'auto-filter' | 'table';
    tableId?: string;
};

export type AutoFilterHeaderButtonPresentation = AutoFilterHeaderButtonProjection & {
    key: string;
    address: string;
    title: string;
    ariaLabel: string;
    expanded: boolean;
};

export type AutoFilterHeaderButtonAnchorRect = {
    left: number;
    bottom: number;
};

export type TableOverlayProjection = {
    id: string;
    name: string;
    range: SpreadsheetRangeAddress;
    rect: AutoFilterScreenRect;
    headerRect: AutoFilterScreenRect | null;
    showFilterButtons: boolean;
    selected: boolean;
};

export type TableContextMenuItemPresentation = {
    label: string;
    title: string;
    action: string;
    checked?: boolean;
};

export type TableContextMenuPresentation = {
    promptTitle: string;
    promptDefaultName: string;
    resizeItem: TableContextMenuItemPresentation;
    renameItem: TableContextMenuItemPresentation;
    styleMenu: {
        label: string;
        title: string;
        items: TableContextMenuItemPresentation[];
    };
    toggleHeaderRowItem: TableContextMenuItemPresentation;
    toggleFilterButtonsItem: TableContextMenuItemPresentation;
    toggleTotalRowItem: TableContextMenuItemPresentation;
    deleteItem: TableContextMenuItemPresentation;
};

export type TableContextMenuEntry =
    | { type: 'separator' }
    | {
        type?: 'item';
        label: string;
        title: string;
        action?: string;
        checked?: boolean;
        submenu?: TableContextMenuItemPresentation[];
    };

export type TableUpsertOptions = {
    tableId?: string;
    name?: string;
    hasHeaders?: boolean;
    showFilterButtons?: boolean;
    showTotals?: boolean;
    styleId?: string | null;
    columns?: SpreadsheetTableDefinition['columns'];
    label?: string;
};

export type TableMutationPlan =
    | {
        kind: 'upsert-table';
        range: SpreadsheetUiRange;
        options: TableUpsertOptions;
    }
    | { kind: 'delete-table'; tableId: string }
    | { kind: 'set-total-row'; table: SpreadsheetTableDefinition; showTotals: boolean }
    | { kind: 'noop' };

export type AutoFilterValueOption = {
    key: string;
    label: string;
    count: number;
    blank: boolean;
    selected: boolean;
};

export type AutoFilterCriteriaDraft = {
    selectedKeys: string[];
    includeBlank: boolean;
    query: string;
    conditions: SpreadsheetAutoFilterCondition[];
    conditionJoin: SpreadsheetAutoFilterConditionJoin;
};

export type AutoFilterMenuState = {
    x: number;
    y: number;
    col: number;
    row: number;
    range: SpreadsheetRangeAddress;
    valuesLoading?: boolean;
    valuesError?: string | null;
    values?: AutoFilterValueOption[];
    draft?: AutoFilterCriteriaDraft;
    valueQuery?: string;
};

export type AutoFilterValueReadPlan = {
    col: number;
    row: number;
    range: ReturnType<typeof autoFilterRangeToUiRange>;
    hasDataRows: boolean;
    read: {
        startCol: number;
        startRow: number;
        endCol: number;
        endRow: number;
        mode: 'computed';
    } | null;
};

export type AutoFilterValueListState = 'loading' | 'error' | 'empty' | 'no-match' | 'ready';

export type AutoFilterMenuPresentation = {
    columnLabel: string;
    filteredValueOptions: AutoFilterValueOption[];
    selectedValueKeys: Set<string>;
    conditionRows: SpreadsheetAutoFilterCondition[];
    valueListState: AutoFilterValueListState;
    valueListTitle: string;
    valueListMessage: string | null;
    hasColumnFilter: boolean;
    hasAnyFilter: boolean;
    allSelected: boolean;
    canApply: boolean;
    hasOperatorCriteria: boolean;
    selectAllDisabled: boolean;
    selectAllTitle: string;
    clearConditionsTitle: string;
    clearColumnTitle: string;
    clearAllTitle: string;
    applyTitle: string;
    sortAscTitle: string;
    sortDescTitle: string;
    searchAriaLabel: string;
    searchTitle: string;
    conditionJoinAriaLabel: string;
    conditionJoinTitle: string;
    removeTitle: string;
    cancelTitle: string;
};

export type AutoFilterMenuAction = 'sort-asc' | 'sort-desc' | 'clear-column' | 'clear-all' | 'remove';

export type AutoFilterMenuActionPlan =
    | {
        kind: 'sort';
        range: ReturnType<typeof autoFilterRangeToUiRange>;
        sortOptions: {
            sortColumns: Array<{ col: number; descending: boolean }>;
            hasHeader: true;
        };
    }
    | {
        kind: 'set-columns';
        columns: NonNullable<SpreadsheetAutoFilter['columns']>;
    }
    | {
        kind: 'remove';
    };

export const AUTO_FILTER_OPERATOR_OPTIONS: Array<{ value: SpreadsheetAutoFilterOperator; label: string }> = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does Not Equal' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'greater_than_or_equal', label: 'Greater Than Or Equal' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'less_than_or_equal', label: 'Less Than Or Equal' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' },
    { value: 'begins_with', label: 'Begins With' },
    { value: 'ends_with', label: 'Ends With' },
];

const TABLE_CONTEXT_MENU_STYLE_ITEMS = [
    { styleId: 'TableStyleLight1', label: 'Light Neutral', action: 'table-style-light' },
    { styleId: 'TableStyleMedium2', label: 'Medium Green', action: 'table-style-medium' },
    { styleId: 'TableStyleMedium4', label: 'Dark Green', action: 'table-style-dark' },
] as const;

export const createEmptyAutoFilterCondition = (): SpreadsheetAutoFilterCondition => ({
    operator: 'contains',
    value: '',
});

const normalizeAutoFilterValue = (value: string): string => String(value ?? '').trim().toLowerCase();

export const autoFilterCriteriaValueKey = (value: SpreadsheetCellValue | null | undefined): string => {
    if (!value || value.kind === 'blank') return '';
    switch (value.kind) {
        case 'text':
            return normalizeAutoFilterValue(value.value);
        case 'number':
            return Number.isInteger(value.value) ? String(Math.trunc(value.value)) : String(value.value);
        case 'boolean':
            return String(value.value);
        case 'datetime':
            return String(value.epoch_ms);
        case 'error':
            return normalizeAutoFilterValue(value.code);
        case 'pending_external':
            return normalizeAutoFilterValue(value.request_id);
        case 'dynamic_array':
            return '';
        default:
            return '';
    }
};

const autoFilterValueLabel = (cell: SpreadsheetRangeCell): string => {
    const displayValue = String(cell.display_value ?? '').trim();
    if (displayValue) return displayValue;
    const rawInput = String(cell.raw_input ?? '').trim();
    if (rawInput && !rawInput.startsWith('=')) return rawInput;
    const value = cell.value;
    if (!value || value.kind === 'blank' || value.kind === 'dynamic_array') return '(Blanks)';
    switch (value.kind) {
        case 'text':
            return value.value || '(Blanks)';
        case 'number':
            return Number.isInteger(value.value) ? String(Math.trunc(value.value)) : String(value.value);
        case 'boolean':
            return value.value ? 'TRUE' : 'FALSE';
        case 'datetime':
            return String(value.epoch_ms);
        case 'error':
            return value.code;
        case 'pending_external':
            return 'Pending external result';
        default:
            return '(Blanks)';
    }
};

export const autoFilterRangeToUiRange = (range: SpreadsheetRangeAddress): {
    startCol: number;
    startRow: number;
    endCol: number;
    endRow: number;
} => ({
    startCol: Math.min(range.start_col, range.end_col),
    startRow: Math.min(range.start_row, range.end_row),
    endCol: Math.max(range.start_col, range.end_col),
    endRow: Math.max(range.start_row, range.end_row),
});

const tableRangeToUiRange = (range: SpreadsheetRangeAddress): SpreadsheetUiRange => autoFilterRangeToUiRange(range);

const normalizedSpreadsheetRange = (range: SpreadsheetRangeAddress): SpreadsheetRangeAddress => {
    const normalized = autoFilterRangeToUiRange(range);
    return {
        sheet_id: range.sheet_id,
        start_col: normalized.startCol,
        start_row: normalized.startRow,
        end_col: normalized.endCol,
        end_row: normalized.endRow,
    };
};

export const clipAutoFilterScreenRect = (
    rect: AutoFilterScreenRect | null | undefined,
    viewport: AutoFilterViewport,
): AutoFilterScreenRect | null => {
    if (!rect) return null;
    const left = Math.max(0, rect.x);
    const top = Math.max(0, rect.y);
    const right = Math.min(viewport.width, rect.x + rect.w);
    const bottom = Math.min(viewport.height, rect.y + rect.h);
    if (right <= left || bottom <= top) return null;
    return {
        x: left,
        y: top,
        w: right - left,
        h: bottom - top,
    };
};

const tableRangeIntersectsVisibleBounds = (
    range: ReturnType<typeof autoFilterRangeToUiRange>,
    visibleBounds: TableOverlayVisibleBounds,
): boolean => (
    range.endCol >= visibleBounds.startCol
    && range.startCol <= visibleBounds.endCol
    && range.endRow >= visibleBounds.startRow
    && range.startRow <= visibleBounds.endRow
);

const tableRangeIntersectsSelection = (
    range: SpreadsheetRangeAddress,
    selectedRange: SpreadsheetUiRange | null | undefined,
): boolean => {
    if (!selectedRange) return false;
    const selectedStartCol = Math.min(selectedRange.startCol, selectedRange.endCol);
    const selectedEndCol = Math.max(selectedRange.startCol, selectedRange.endCol);
    const selectedStartRow = Math.min(selectedRange.startRow, selectedRange.endRow);
    const selectedEndRow = Math.max(selectedRange.startRow, selectedRange.endRow);
    return (
        selectedEndCol >= range.start_col
        && selectedStartCol <= range.end_col
        && selectedEndRow >= range.start_row
        && selectedStartRow <= range.end_row
    );
};

export const buildTableOverlayProjections = (input: {
    tables: Record<string, SpreadsheetTableDefinition> | null | undefined;
    visibleBounds: TableOverlayVisibleBounds;
    viewport: AutoFilterViewport;
    getRangeScreenRect: (range: { start: { col: number; row: number }; end: { col: number; row: number } }) => AutoFilterScreenRect | null;
    selectedRange?: SpreadsheetUiRange | null;
}): TableOverlayProjection[] => {
    const { tables, visibleBounds, viewport, getRangeScreenRect, selectedRange } = input;
    return Object.values(tables ?? {})
        .map((table): TableOverlayProjection | null => {
            const uiRange = autoFilterRangeToUiRange(table.range);
            if (!tableRangeIntersectsVisibleBounds(uiRange, visibleBounds)) return null;
            const range = normalizedSpreadsheetRange(table.range);
            const rect = clipAutoFilterScreenRect(getRangeScreenRect({
                start: { col: range.start_col, row: range.start_row },
                end: { col: range.end_col, row: range.end_row },
            }), viewport);
            if (!rect) return null;
            const headerRect = table.has_headers === false
                ? null
                : clipAutoFilterScreenRect(getRangeScreenRect({
                    start: { col: range.start_col, row: range.start_row },
                    end: { col: range.end_col, row: range.start_row },
                }), viewport);
            return {
                id: table.id,
                name: table.name || 'Table',
                range,
                rect,
                headerRect,
                showFilterButtons: table.show_filter_buttons !== false,
                selected: tableRangeIntersectsSelection(range, selectedRange),
            };
        })
        .filter((entry): entry is TableOverlayProjection => Boolean(entry));
};

export const tableStyleIdForContextMenuAction = (action: string): string | null => (
    TABLE_CONTEXT_MENU_STYLE_ITEMS.find((item) => item.action === action)?.styleId ?? null
);

export const buildTableContextMenuPresentation = (
    table: SpreadsheetTableDefinition,
): TableContextMenuPresentation => {
    const tableName = String(table.name || 'Table');
    const titleName = table.name ? table.name : 'this table';
    const currentStyleId = table.style_id ?? 'TableStyleMedium2';
    return {
        promptTitle: 'Table name',
        promptDefaultName: String(table.name ?? ''),
        resizeItem: {
            label: `Resize ${tableName} to Selection`,
            title: `Resize ${titleName} through the engine-owned UpsertTable operation.`,
            action: 'resize-table',
        },
        renameItem: {
            label: `Rename ${tableName}`,
            title: `Rename ${titleName} through the engine-owned UpsertTable operation.`,
            action: 'rename-table',
        },
        styleMenu: {
            label: 'Table Style',
            title: `Change ${titleName} style metadata through the engine-owned UpsertTable operation.`,
            items: TABLE_CONTEXT_MENU_STYLE_ITEMS.map((style) => ({
                label: style.label,
                checked: currentStyleId === style.styleId,
                action: style.action,
                title: `Apply ${style.label} as table style metadata without writing cell formats.`,
            })),
        },
        toggleHeaderRowItem: {
            label: table.has_headers === false ? 'Show Header Row' : 'Hide Header Row',
            title: `${table.has_headers === false ? 'Show' : 'Hide'} the header row on ${titleName} through the engine-owned UpsertTable operation.`,
            action: 'toggle-table-header-row',
        },
        toggleFilterButtonsItem: {
            label: table.show_filter_buttons === false ? 'Show Filter Buttons' : 'Hide Filter Buttons',
            title: `${table.show_filter_buttons === false ? 'Show' : 'Hide'} filter buttons on ${titleName} through the engine-owned UpsertTable operation.`,
            action: 'toggle-table-filter-buttons',
        },
        toggleTotalRowItem: {
            label: table.show_totals === true ? 'Hide Total Row' : 'Show Total Row',
            title: `${table.show_totals === true ? 'Hide' : 'Show'} the total row on ${titleName} through the engine-owned UpsertTable operation.`,
            action: 'toggle-table-total-row',
        },
        deleteItem: {
            label: `Delete ${tableName}`,
            title: `Delete ${titleName} metadata through the engine-owned DeleteTable operation. Cell values remain in place.`,
            action: 'delete-table',
        },
    };
};

export const buildTableContextMenuEntries = (
    presentation: TableContextMenuPresentation | null | undefined,
): TableContextMenuEntry[] => {
    if (!presentation) return [];
    return [
        { type: 'separator' },
        presentation.resizeItem,
        presentation.renameItem,
        {
            label: presentation.styleMenu.label,
            title: presentation.styleMenu.title,
            submenu: presentation.styleMenu.items,
        },
        presentation.toggleHeaderRowItem,
        presentation.toggleFilterButtonsItem,
        presentation.toggleTotalRowItem,
        presentation.deleteItem,
    ];
};

const buildProjectedTableUpsertPlan = (
    table: SpreadsheetTableDefinition | null | undefined,
    options: {
        name?: string;
        hasHeaders?: boolean;
        showFilterButtons?: boolean;
        showTotals?: boolean;
        styleId?: string | null;
        preserveColumns?: boolean;
        label: string;
    },
): TableMutationPlan => {
    if (!table?.range) return { kind: 'noop' };
    return {
        kind: 'upsert-table',
        range: tableRangeToUiRange(table.range),
        options: {
            tableId: table.id,
            name: options.name ?? table.name,
            hasHeaders: options.hasHeaders ?? (table.has_headers !== false),
            showFilterButtons: options.showFilterButtons ?? (table.show_filter_buttons !== false),
            showTotals: options.showTotals ?? (table.show_totals === true),
            styleId: options.styleId ?? table.style_id ?? null,
            columns: options.preserveColumns === false ? undefined : table.columns,
            label: options.label,
        },
    };
};

export const buildCreateTableFromSelectionPlan = (
    range: SpreadsheetUiRange | null | undefined,
    styleId: string = 'TableStyleMedium2',
): TableMutationPlan => (
    range
        ? {
            kind: 'upsert-table',
            range,
            options: {
                hasHeaders: true,
                showFilterButtons: true,
                styleId: String(styleId ?? '').trim() || 'TableStyleMedium2',
            },
        }
        : { kind: 'noop' }
);

export const buildResizeTableToSelectionPlan = (
    table: SpreadsheetTableDefinition | null | undefined,
    range: SpreadsheetUiRange | null | undefined,
): TableMutationPlan => {
    if (!table || !range) return { kind: 'noop' };
    return {
        kind: 'upsert-table',
        range,
        options: {
            tableId: table.id,
            name: table.name,
            hasHeaders: table.has_headers !== false,
            showFilterButtons: table.show_filter_buttons !== false,
            showTotals: table.show_totals === true,
            styleId: table.style_id ?? null,
            columns: table.columns,
        },
    };
};

export const buildToggleTableHeaderRowPlan = (
    table: SpreadsheetTableDefinition | null | undefined,
): TableMutationPlan => {
    if (!table) return { kind: 'noop' };
    const hasHeaders = table.has_headers === false;
    return buildProjectedTableUpsertPlan(table, {
        hasHeaders,
        preserveColumns: false,
        label: hasHeaders ? 'Show table header row' : 'Hide table header row',
    });
};

export const buildToggleTableFilterButtonsPlan = (
    table: SpreadsheetTableDefinition | null | undefined,
): TableMutationPlan => {
    if (!table) return { kind: 'noop' };
    const showFilterButtons = table.show_filter_buttons === false;
    return buildProjectedTableUpsertPlan(table, {
        showFilterButtons,
        preserveColumns: true,
        label: showFilterButtons ? 'Show table filter buttons' : 'Hide table filter buttons',
    });
};

export const buildRenameTablePlan = (
    table: SpreadsheetTableDefinition | null | undefined,
    nextName: string,
): TableMutationPlan => {
    const name = String(nextName ?? '').trim();
    if (!table || !name || name === table.name) return { kind: 'noop' };
    return buildProjectedTableUpsertPlan(table, {
        name,
        preserveColumns: true,
        label: 'Rename table',
    });
};

export const buildSetTableStylePlan = (
    table: SpreadsheetTableDefinition | null | undefined,
    styleId: string,
): TableMutationPlan => {
    const normalizedStyleId = String(styleId ?? '').trim();
    if (!table || !normalizedStyleId || normalizedStyleId === table.style_id) return { kind: 'noop' };
    return buildProjectedTableUpsertPlan(table, {
        styleId: normalizedStyleId,
        preserveColumns: true,
        label: 'Change table style',
    });
};

export const buildToggleTableTotalRowPlan = (
    table: SpreadsheetTableDefinition | null | undefined,
): TableMutationPlan => (
    table
        ? { kind: 'set-total-row', table, showTotals: table.show_totals !== true }
        : { kind: 'noop' }
);

export const buildDeleteTablePlan = (
    table: SpreadsheetTableDefinition | null | undefined,
): TableMutationPlan => (
    table ? { kind: 'delete-table', tableId: table.id } : { kind: 'noop' }
);

export const resolveActiveAutoFilterForMenu = (
    menu: AutoFilterMenuState | null | undefined,
    activeSheetAutoFilter: SpreadsheetAutoFilter | null | undefined,
): SpreadsheetAutoFilter | null => {
    if (!menu) return null;
    if (activeSheetAutoFilter?.range && rangesEqual(activeSheetAutoFilter.range, menu.range)) {
        return activeSheetAutoFilter;
    }
    return { range: menu.range, columns: [] };
};

export const isAutoFilterMenuTargetValid = (
    menu: AutoFilterMenuState | null | undefined,
): boolean => {
    if (!menu) return false;
    const range = autoFilterRangeToUiRange(menu.range);
    return menu.col >= range.startCol
        && menu.col <= range.endCol
        && menu.row === range.startRow;
};

const sameAutoFilterMenuTarget = (
    menu: AutoFilterMenuState | null | undefined,
    plan: Pick<AutoFilterValueReadPlan, 'col' | 'row'>,
): menu is AutoFilterMenuState => Boolean(menu && menu.col === plan.col && menu.row === plan.row);

export const buildAutoFilterValueReadPlan = (
    menu: AutoFilterMenuState,
    autoFilter: SpreadsheetAutoFilter,
): AutoFilterValueReadPlan => {
    const range = autoFilterRangeToUiRange(autoFilter.range);
    const hasDataRows = range.startRow < range.endRow;
    return {
        col: menu.col,
        row: menu.row,
        range,
        hasDataRows,
        read: hasDataRows
            ? {
                startCol: menu.col,
                startRow: range.startRow + 1,
                endCol: menu.col,
                endRow: range.endRow,
                mode: 'computed',
            }
            : null,
    };
};

export const markAutoFilterValueLoadStarted = (
    menu: AutoFilterMenuState | null,
    plan: Pick<AutoFilterValueReadPlan, 'col' | 'row'>,
): AutoFilterMenuState | null => (
    sameAutoFilterMenuTarget(menu, plan)
        ? { ...menu, valuesLoading: true, valuesError: null, values: menu.values ?? [] }
        : menu
);

export const applyAutoFilterEmptyValueLoad = (
    menu: AutoFilterMenuState | null,
    autoFilter: SpreadsheetAutoFilter,
    plan: Pick<AutoFilterValueReadPlan, 'col' | 'row'>,
): AutoFilterMenuState | null => (
    sameAutoFilterMenuTarget(menu, plan)
        ? {
            ...menu,
            valuesLoading: false,
            valuesError: null,
            values: [],
            draft: createAutoFilterCriteriaDraft(autoFilter, plan.col, []),
            valueQuery: '',
        }
        : menu
);

export const applyAutoFilterValueLoadSuccess = (
    menu: AutoFilterMenuState | null,
    autoFilter: SpreadsheetAutoFilter,
    plan: Pick<AutoFilterValueReadPlan, 'col' | 'row'>,
    cells: SpreadsheetRangeCell[][],
): AutoFilterMenuState | null => {
    if (!sameAutoFilterMenuTarget(menu, plan)) return menu;
    const values = buildAutoFilterValueOptions({
        autoFilter,
        col: plan.col,
        cells,
    });
    return {
        ...menu,
        valuesLoading: false,
        valuesError: null,
        values,
        draft: createAutoFilterCriteriaDraft(autoFilter, plan.col, values, menu.valueQuery ?? ''),
        valueQuery: menu.valueQuery ?? '',
    };
};

export const applyAutoFilterValueLoadError = (
    menu: AutoFilterMenuState | null,
    plan: Pick<AutoFilterValueReadPlan, 'col' | 'row'>,
    error: unknown,
): AutoFilterMenuState | null => (
    sameAutoFilterMenuTarget(menu, plan)
        ? {
            ...menu,
            valuesLoading: false,
            valuesError: error instanceof Error ? error.message : String(error),
            values: [],
        }
        : menu
);

export const updateAutoFilterMenuValueQuery = (
    menu: AutoFilterMenuState | null,
    query: string,
): AutoFilterMenuState | null => (
    menu
        ? {
            ...menu,
            valueQuery: query,
            draft: menu.draft ? { ...menu.draft, query } : menu.draft,
        }
        : menu
);

export const toggleAutoFilterMenuDraftOption = (
    menu: AutoFilterMenuState | null,
    option: AutoFilterValueOption,
): AutoFilterMenuState | null => (
    menu?.draft
        ? { ...menu, draft: toggleAutoFilterDraftOption(menu.draft, option) }
        : menu
);

export const setAutoFilterMenuDraftAllOptions = (
    menu: AutoFilterMenuState | null,
    selected: boolean,
): AutoFilterMenuState | null => (
    menu
        ? {
            ...menu,
            draft: setAutoFilterDraftAllOptions(
                menu.values ?? [],
                selected,
                menu.valueQuery ?? '',
                menu.draft
                    ? { conditions: menu.draft.conditions, conditionJoin: menu.draft.conditionJoin }
                    : undefined,
            ),
        }
        : menu
);

const updateAutoFilterMenuCondition = (
    menu: AutoFilterMenuState | null,
    index: number,
    patch: Partial<SpreadsheetAutoFilterCondition>,
): AutoFilterMenuState | null => {
    if (!menu?.draft || index < 0 || index > 1) return menu;
    const conditions = menu.draft.conditions.slice(0, 2);
    while (conditions.length <= index) {
        conditions.push(createEmptyAutoFilterCondition());
    }
    conditions[index] = {
        ...conditions[index],
        ...patch,
    };
    return {
        ...menu,
        draft: { ...menu.draft, conditions },
    };
};

export const setAutoFilterMenuConditionOperator = (
    menu: AutoFilterMenuState | null,
    index: number,
    operator: SpreadsheetAutoFilterOperator,
): AutoFilterMenuState | null => updateAutoFilterMenuCondition(menu, index, { operator });

export const setAutoFilterMenuConditionValue = (
    menu: AutoFilterMenuState | null,
    index: number,
    value: string,
): AutoFilterMenuState | null => updateAutoFilterMenuCondition(menu, index, { value });

export const setAutoFilterMenuConditionJoin = (
    menu: AutoFilterMenuState | null,
    conditionJoin: SpreadsheetAutoFilterConditionJoin,
): AutoFilterMenuState | null => (
    menu?.draft
        ? { ...menu, draft: { ...menu.draft, conditionJoin } }
        : menu
);

export const clearAutoFilterMenuConditions = (
    menu: AutoFilterMenuState | null,
): AutoFilterMenuState | null => (
    menu?.draft
        ? { ...menu, draft: { ...menu.draft, conditions: [] } }
        : menu
);

export const buildAutoFilterMenuPresentation = (
    menu: AutoFilterMenuState | null | undefined,
    autoFilter: SpreadsheetAutoFilter | null | undefined,
): AutoFilterMenuPresentation | null => {
    if (!menu) return null;

    const columnLabel = colToLetter(menu.col);
    const values = menu.values ?? [];
    const filteredValueOptions = filterAutoFilterValueOptions(values, menu.valueQuery ?? '');
    const conditions = menu.draft?.conditions ?? [];
    const hasColumnFilter = Boolean(autoFilter && isAutoFilterColumnFiltered(autoFilter, menu.col));
    const hasAnyFilter = Boolean(autoFilter?.columns?.some((column) => isAutoFilterColumnFiltered(autoFilter, column.col)));
    const hasOperatorCriteria = Boolean(conditions.some((condition) => String(condition.value ?? '').trim().length > 0));
    const valueListState: AutoFilterValueListState = menu.valuesLoading
        ? 'loading'
        : menu.valuesError
            ? 'error'
            : values.length === 0
                ? 'empty'
                : filteredValueOptions.length === 0
                    ? 'no-match'
                    : 'ready';
    const valueListPresentation = (() => {
        switch (valueListState) {
            case 'loading':
                return {
                    valueListTitle: 'Reading computed values from the spreadsheet engine.',
                    valueListMessage: 'Loading values...',
                };
            case 'error':
                return {
                    valueListTitle: menu.valuesError ?? 'Unable to load computed values from the spreadsheet engine.',
                    valueListMessage: 'Unable to load values',
                };
            case 'empty':
                return {
                    valueListTitle: 'The AutoFilter range has no data rows for this column.',
                    valueListMessage: 'No values in filter range',
                };
            case 'no-match':
                return {
                    valueListTitle: 'No computed values match the current search.',
                    valueListMessage: 'No matching values',
                };
            case 'ready':
                return {
                    valueListTitle: '',
                    valueListMessage: null,
                };
        }
    })();

    return {
        columnLabel,
        filteredValueOptions,
        selectedValueKeys: new Set(menu.draft?.selectedKeys ?? []),
        conditionRows: [
            conditions[0] ?? createEmptyAutoFilterCondition(),
            conditions[1] ?? createEmptyAutoFilterCondition(),
        ],
        valueListState,
        ...valueListPresentation,
        hasColumnFilter,
        hasAnyFilter,
        allSelected: Boolean(menu.draft && isAutoFilterDraftAllSelected(menu.draft, values)),
        canApply: Boolean(menu.draft && autoFilterDraftHasSelection(menu.draft)),
        hasOperatorCriteria,
        selectAllDisabled: values.length === 0,
        selectAllTitle: Boolean(menu.draft && isAutoFilterDraftAllSelected(menu.draft, values))
            ? 'Clear all value checkboxes.'
            : 'Select every available value.',
        clearConditionsTitle: hasOperatorCriteria
            ? 'Clear the engine-owned AutoFilter operator criteria.'
            : 'No operator criteria are active.',
        clearColumnTitle: hasColumnFilter
            ? `Remove the AutoFilter criteria for column ${columnLabel}.`
            : `Column ${columnLabel} does not have active criteria.`,
        clearAllTitle: hasAnyFilter
            ? 'Remove all criteria while keeping the AutoFilter range.'
            : 'No AutoFilter criteria are active.',
        applyTitle: Boolean(menu.draft && autoFilterDraftHasSelection(menu.draft))
            ? 'Apply the selected values as a Rust engine AutoFilter criterion.'
            : 'Select at least one value before applying.',
        sortAscTitle: `Sort the engine-owned AutoFilter range by ${columnLabel}, keeping the header row in place.`,
        sortDescTitle: `Sort the engine-owned AutoFilter range by ${columnLabel} descending, keeping the header row in place.`,
        searchAriaLabel: `Search values in ${columnLabel}`,
        searchTitle: `Search computed values in column ${columnLabel}.`,
        conditionJoinAriaLabel: `Condition join for ${columnLabel}`,
        conditionJoinTitle: `Choose whether ${columnLabel} must match both criteria or either criterion.`,
        removeTitle: 'Remove the AutoFilter range and clear only filter-owned hidden rows.',
        cancelTitle: 'Close without changing the engine-owned AutoFilter criteria.',
    };
};

export const buildAutoFilterMenuActionPlan = (
    action: AutoFilterMenuAction,
    menu: AutoFilterMenuState | null | undefined,
    autoFilter: SpreadsheetAutoFilter | null | undefined,
): AutoFilterMenuActionPlan | null => {
    if (!menu || !autoFilter) return null;
    const range = autoFilterRangeToUiRange(autoFilter.range);
    switch (action) {
        case 'sort-asc':
            return {
                kind: 'sort',
                range,
                sortOptions: {
                    sortColumns: [{ col: menu.col, descending: false }],
                    hasHeader: true,
                },
            };
        case 'sort-desc':
            return {
                kind: 'sort',
                range,
                sortOptions: {
                    sortColumns: [{ col: menu.col, descending: true }],
                    hasHeader: true,
                },
            };
        case 'clear-column':
            return {
                kind: 'set-columns',
                columns: autoFilterColumnsWithout(autoFilter, menu.col),
            };
        case 'clear-all':
            return {
                kind: 'set-columns',
                columns: [],
            };
        case 'remove':
            return { kind: 'remove' };
    }
};

export const buildAutoFilterApplyDraftPlan = (
    menu: AutoFilterMenuState | null | undefined,
    autoFilter: SpreadsheetAutoFilter | null | undefined,
): AutoFilterMenuActionPlan | null => {
    if (!menu?.draft || !autoFilter) return null;
    if (!buildAutoFilterMenuPresentation(menu, autoFilter)?.canApply) return null;
    return {
        kind: 'set-columns',
        columns: buildAutoFilterColumnsFromDraft(
            autoFilter,
            menu.col,
            menu.draft,
            menu.values ?? [],
        ),
    };
};

const rangesEqual = (
    a: SpreadsheetRangeAddress | null | undefined,
    b: SpreadsheetRangeAddress | null | undefined,
): boolean => {
    if (!a || !b) return false;
    const left = autoFilterRangeToUiRange(a);
    const right = autoFilterRangeToUiRange(b);
    return (
        String(a.sheet_id ?? '') === String(b.sheet_id ?? '') &&
        left.startCol === right.startCol &&
        left.startRow === right.startRow &&
        left.endCol === right.endCol &&
        left.endRow === right.endRow
    );
};

export const tableFilterRange = (table: SpreadsheetTableDefinition): SpreadsheetRangeAddress | null => {
    if (!table?.range || table.has_headers === false || table.show_filter_buttons === false) return null;
    const range = autoFilterRangeToUiRange(table.range);
    const endRow = table.show_totals === true
        ? Math.max(range.startRow, range.endRow - 1)
        : range.endRow;
    return {
        sheet_id: table.range.sheet_id,
        start_row: range.startRow,
        start_col: range.startCol,
        end_row: endRow,
        end_col: range.endCol,
    };
};

export const isAutoFilterColumnFiltered = (autoFilter: SpreadsheetAutoFilter | null | undefined, col: number): boolean => {
    const criteria = autoFilter?.columns?.find((column) => column.col === col);
    if (!criteria) return false;
    return (
        criteria.blank === true
        || (Array.isArray(criteria.values) && criteria.values.length > 0)
        || (Array.isArray(criteria.conditions) && criteria.conditions.length > 0)
    );
};

export const autoFilterColumnsWithout = (
    autoFilter: SpreadsheetAutoFilter,
    col: number,
): NonNullable<SpreadsheetAutoFilter['columns']> => (
    (autoFilter.columns ?? []).filter((column) => column.col !== col)
);

export const buildAutoFilterValueOptions = (input: {
    autoFilter: SpreadsheetAutoFilter;
    col: number;
    cells: SpreadsheetRangeCell[][];
    maxOptions?: number;
}): AutoFilterValueOption[] => {
    const { autoFilter, col, cells } = input;
    const maxOptions = Math.max(1, Math.floor(input.maxOptions ?? Number.MAX_SAFE_INTEGER));
    const currentCriteria = autoFilter.columns?.find((column) => column.col === col) ?? null;
    const hasActiveCriteria = Boolean(
        currentCriteria && (
            currentCriteria.blank === true
            || (currentCriteria.values?.length ?? 0) > 0
            || (currentCriteria.conditions?.length ?? 0) > 0
        )
    );
    const activeValues = new Set((currentCriteria?.values ?? []).map(normalizeAutoFilterValue));
    const byKey = new Map<string, AutoFilterValueOption>();

    for (const row of cells) {
        for (const cell of row) {
            if (cell.col !== col) continue;
            const key = autoFilterCriteriaValueKey(cell.value);
            const blank = key.length === 0;
            const existing = byKey.get(key);
            if (existing) {
                existing.count += 1;
                continue;
            }
            byKey.set(key, {
                key,
                label: blank ? '(Blanks)' : autoFilterValueLabel(cell),
                count: 1,
                blank,
                selected: !hasActiveCriteria || (blank ? currentCriteria?.blank === true : activeValues.has(key)),
            });
        }
    }

    return Array.from(byKey.values())
        .sort((a, b) => {
            if (a.blank !== b.blank) return a.blank ? 1 : -1;
            return a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true });
        })
        .slice(0, maxOptions);
};

export const createAutoFilterCriteriaDraft = (
    autoFilter: SpreadsheetAutoFilter,
    col: number,
    options: AutoFilterValueOption[],
    query = '',
): AutoFilterCriteriaDraft => {
    const currentCriteria = autoFilter.columns?.find((column) => column.col === col) ?? null;
    const hasActiveCriteria = Boolean(
        currentCriteria && (
            currentCriteria.blank === true
            || (currentCriteria.values?.length ?? 0) > 0
            || (currentCriteria.conditions?.length ?? 0) > 0
        )
    );
    if (!hasActiveCriteria) {
        return {
            selectedKeys: options.filter((option) => !option.blank).map((option) => option.key),
            includeBlank: options.some((option) => option.blank),
            query,
            conditions: [],
            conditionJoin: 'and',
        };
    }

    const activeValues = new Set((currentCriteria?.values ?? []).map(normalizeAutoFilterValue));
    return {
        selectedKeys: options
            .filter((option) => !option.blank && activeValues.has(option.key))
            .map((option) => option.key),
        includeBlank: currentCriteria?.blank === true && options.some((option) => option.blank),
        query,
        conditions: normalizeAutoFilterDraftConditions(currentCriteria?.conditions ?? []),
        conditionJoin: currentCriteria?.condition_join === 'or' ? 'or' : 'and',
    };
};

export const filterAutoFilterValueOptions = (
    options: AutoFilterValueOption[],
    query: string,
): AutoFilterValueOption[] => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter((option) => (
        option.label.toLowerCase().includes(normalizedQuery)
        || option.key.toLowerCase().includes(normalizedQuery)
    ));
};

export const toggleAutoFilterDraftOption = (
    draft: AutoFilterCriteriaDraft,
    option: AutoFilterValueOption,
): AutoFilterCriteriaDraft => {
    if (option.blank) {
        return { ...draft, includeBlank: !draft.includeBlank };
    }
    const selected = new Set(draft.selectedKeys);
    if (selected.has(option.key)) {
        selected.delete(option.key);
    } else {
        selected.add(option.key);
    }
    return { ...draft, selectedKeys: Array.from(selected) };
};

export const setAutoFilterDraftAllOptions = (
    options: AutoFilterValueOption[],
    selected: boolean,
    query = '',
    existing?: Pick<AutoFilterCriteriaDraft, 'conditions' | 'conditionJoin'>,
): AutoFilterCriteriaDraft => ({
        selectedKeys: selected ? options.filter((option) => !option.blank).map((option) => option.key) : [],
        includeBlank: selected && options.some((option) => option.blank),
        query,
        conditions: existing?.conditions ?? [],
        conditionJoin: existing?.conditionJoin ?? 'and',
    });

export const isAutoFilterDraftAllSelected = (
    draft: AutoFilterCriteriaDraft,
    options: AutoFilterValueOption[],
): boolean => {
    const selected = new Set(draft.selectedKeys);
    return options.every((option) => (
        option.blank ? draft.includeBlank : selected.has(option.key)
    ));
};

export const autoFilterDraftHasSelection = (draft: AutoFilterCriteriaDraft): boolean => (
    draft.includeBlank || draft.selectedKeys.length > 0 || normalizeAutoFilterDraftConditions(draft.conditions).length > 0
);

export const normalizeAutoFilterDraftConditions = (
    conditions: SpreadsheetAutoFilterCondition[],
): SpreadsheetAutoFilterCondition[] => (
    conditions
        .map((condition) => ({
            operator: condition.operator,
            value: String(condition.value ?? '').trim(),
        }))
        .filter((condition) => condition.value.length > 0)
        .slice(0, 2)
);

export const buildAutoFilterColumnsFromDraft = (
    autoFilter: SpreadsheetAutoFilter,
    col: number,
    draft: AutoFilterCriteriaDraft,
    options: AutoFilterValueOption[],
): NonNullable<SpreadsheetAutoFilter['columns']> => {
    const columns = autoFilterColumnsWithout(autoFilter, col);
    const conditions = normalizeAutoFilterDraftConditions(draft.conditions);
    if (!autoFilterDraftHasSelection(draft)) {
        return columns;
    }
    const allValuesSelected = isAutoFilterDraftAllSelected(draft, options);
    const nextColumn: NonNullable<SpreadsheetAutoFilter['columns']>[number] = {
        col,
    };
    if (!allValuesSelected) {
        if (draft.selectedKeys.length > 0) {
            nextColumn.values = draft.selectedKeys;
        }
        if (draft.includeBlank) {
            nextColumn.blank = true;
        }
    }
    if (conditions.length > 0) {
        nextColumn.conditions = conditions;
        nextColumn.condition_join = draft.conditionJoin === 'or' ? 'or' : 'and';
    }
    if (
        nextColumn.blank !== true
        && (nextColumn.values?.length ?? 0) === 0
        && (nextColumn.conditions?.length ?? 0) === 0
    ) {
        return columns;
    }
    columns.push(nextColumn);
    return columns;
};

export const buildAutoFilterHeaderButtons = (input: {
    autoFilter: SpreadsheetAutoFilter | null | undefined;
    visibleBounds: AutoFilterVisibleBounds;
    viewport: AutoFilterViewport;
    getRangeScreenRect: (range: { start: { col: number; row: number }; end: { col: number; row: number } }) => AutoFilterScreenRect | null;
}): AutoFilterHeaderButtonProjection[] => {
    const { autoFilter, visibleBounds, viewport, getRangeScreenRect } = input;
    if (!autoFilter?.range) return [];

    const range = autoFilterRangeToUiRange(autoFilter.range);
    const firstCol = Math.max(range.startCol, visibleBounds.startCol);
    const lastCol = Math.min(range.endCol, visibleBounds.endCol);
    if (lastCol < firstCol) return [];

    const buttons: AutoFilterHeaderButtonProjection[] = [];
    for (let col = firstCol; col <= lastCol; col++) {
        const rect = getRangeScreenRect({
            start: { col, row: range.startRow },
            end: { col, row: range.startRow },
        });
        const clipped = clipAutoFilterScreenRect(rect, viewport);
        if (!clipped) continue;
        const left = clipped.x;
        const top = clipped.y;
        const right = clipped.x + clipped.w;
        const bottom = clipped.y + clipped.h;
        if (clipped.w < 22 || clipped.h < 18) continue;
        buttons.push({
            col,
            row: range.startRow,
            x: Math.max(left + 2, right - 22),
            y: Math.max(top + 2, Math.min(bottom - 20, top + Math.max(2, (bottom - top - 18) / 2))),
            filtered: isAutoFilterColumnFiltered(autoFilter, col),
            range: autoFilter.range,
            source: 'auto-filter',
        });
    }
    return buttons;
};

export const buildTableFilterHeaderButtons = (input: {
    tables: Record<string, SpreadsheetTableDefinition> | null | undefined;
    activeAutoFilter?: SpreadsheetAutoFilter | null;
    visibleBounds: AutoFilterVisibleBounds;
    viewport: AutoFilterViewport;
    getRangeScreenRect: (range: { start: { col: number; row: number }; end: { col: number; row: number } }) => AutoFilterScreenRect | null;
}): AutoFilterHeaderButtonProjection[] => {
    const { tables, activeAutoFilter, visibleBounds, viewport, getRangeScreenRect } = input;
    const buttons: AutoFilterHeaderButtonProjection[] = [];
    for (const table of Object.values(tables ?? {})) {
        const filterRange = tableFilterRange(table);
        if (!filterRange || rangesEqual(activeAutoFilter?.range, filterRange)) continue;
        const range = autoFilterRangeToUiRange(filterRange);
        const firstCol = Math.max(range.startCol, visibleBounds.startCol);
        const lastCol = Math.min(range.endCol, visibleBounds.endCol);
        if (lastCol < firstCol) continue;
        for (let col = firstCol; col <= lastCol; col++) {
            const rect = getRangeScreenRect({
                start: { col, row: range.startRow },
                end: { col, row: range.startRow },
            });
            const clipped = clipAutoFilterScreenRect(rect, viewport);
            if (!clipped) continue;
            const left = clipped.x;
            const top = clipped.y;
            const right = clipped.x + clipped.w;
            const bottom = clipped.y + clipped.h;
            if (clipped.w < 22 || clipped.h < 18) continue;
            buttons.push({
                col,
                row: range.startRow,
                x: Math.max(left + 2, right - 22),
                y: Math.max(top + 2, Math.min(bottom - 20, top + Math.max(2, (bottom - top - 18) / 2))),
                filtered: false,
                range: filterRange,
                source: 'table',
                tableId: table.id,
            });
        }
    }
    return buttons;
};

export const buildAutoFilterHeaderButtonPresentation = (input: {
    button: AutoFilterHeaderButtonProjection;
    activeMenu?: AutoFilterMenuState | null;
}): AutoFilterHeaderButtonPresentation => {
    const { button, activeMenu } = input;
    const address = `${colToLetter(button.col)}${button.row + 1}`;
    const title = button.filtered
        ? `Filter menu for ${address}. This column has active engine AutoFilter criteria.`
        : button.source === 'table'
            ? `Filter menu for ${address}. Sort or filter this engine-owned table column.`
            : `Filter menu for ${address}. Sort or filter this engine-owned AutoFilter column.`;
    return {
        ...button,
        key: `${button.source}:${button.tableId ?? 'sheet'}:${button.row}:${button.col}`,
        address,
        title,
        ariaLabel: title,
        expanded: activeMenu?.col === button.col && activeMenu?.row === button.row,
    };
};

export const buildAutoFilterMenuTogglePlan = (input: {
    button: AutoFilterHeaderButtonProjection;
    currentMenu?: AutoFilterMenuState | null;
    anchorRect: AutoFilterHeaderButtonAnchorRect;
}): AutoFilterMenuState | null => {
    const { button, currentMenu, anchorRect } = input;
    if (currentMenu?.col === button.col && currentMenu?.row === button.row) return null;
    return {
        x: anchorRect.left,
        y: anchorRect.bottom + 4,
        col: button.col,
        row: button.row,
        range: button.range,
        valuesLoading: true,
        values: [],
        valueQuery: '',
    };
};
